import React from "react";
import { FieldArray } from "react-final-form-arrays";
import { Field } from "react-final-form";
import {
  MouthpieceBaffleTypeSelectField,
  mouthpieceTipOpenings,
  InstrumentTypeSelectField,
} from "@syos-music/instruments";
import { Fieldset, TextField, SelectField } from "@syos-music/form";

import {
  Box,
  Grid,
  MenuItem,
  Typography,
  Collapse,
  InputAdornment,
} from "@mui/material";

import { saxophone as saxophoneMouthpieceUtils } from "components/mouthpiece/utils";

const floatFieldProps = {
  parse: (value) => {
    const result = parseFloat(value);
    return isNaN(result) ? value : result;
  },
};

const integerFieldProps = {
  parse: (value) => {
    const result = parseInt(value, 10);
    return isNaN(result) ? value : result;
  },
};

const safe =
  (fn) =>
  (...args) => {
    try {
      return fn(...args);
    } catch (err) {
      // console.error(err);
      return null;
    }
  };

export default function SaxophoneMouthpieceField({ name }) {
  return (
    <Field name={name}>
      {({ input: { value, onChange } }) => {
        const baffleTypeOptions = value.saxophoneType
          ? saxophoneMouthpieceUtils.getBaffleTypes(value.saxophoneType)
          : [];

        const chamberTypeOptions = value.saxophoneType
          ? saxophoneMouthpieceUtils.getChamberTypes(value.saxophoneType)
          : [];

        const chamberSizeNameOptions =
          saxophoneMouthpieceUtils.getChamberSizeNames();

        const tipOneningRange = value.saxophoneType
          ? saxophoneMouthpieceUtils.getTipOpeningRange(value.saxophoneType)
          : [];

        return (
          <Grid container alignContent="flex-start">
            <Grid item xs={12}>
              <InstrumentTypeSelectField
                instrument="saxophone"
                name={`${name}.saxophoneType`}
                required
                onChange={(evt) => {
                  const saxophoneType = evt.target.value;
                  if (saxophoneType === value.saxophoneType) return;

                  const defaults =
                    saxophoneMouthpieceUtils.applyMouthpieceDefaults({
                      saxophoneType,
                    });

                  onChange(defaults);
                }}
                label="Type de saxophone"
                margin="normal"
                validate={(saxophoneType) => {
                  if (
                    !saxophoneMouthpieceUtils.isValidSaxophoneType(
                      saxophoneType
                    )
                  )
                    return "Type de saxophone invalide";
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Collapse in={!!value.saxophoneType}>
                <Grid container>
                  <Grid item xs={12}>
                    <Fieldset legend="Plafond" margin="normal">
                      <Grid container>
                        <Grid item xs={12}>
                          <MouthpieceBaffleTypeSelectField
                            instrument="saxophone"
                            instrumentType={value.saxophoneType}
                            name={`${name}.baffle.type`}
                            required
                            validate={(baffleType) => {
                              if (
                                !saxophoneMouthpieceUtils.isValidBaffleType(
                                  baffleType,
                                  value.saxophoneType
                                )
                              )
                                return "Type de plafond invalide";
                            }}
                            onChange={(evt) => {
                              const baffleType = evt.target.value;

                              let newValue = {
                                ...value,
                                baffle: {
                                  ...value.baffle,
                                  type: baffleType,
                                },
                              };

                              if (
                                baffleType !==
                                (value.baffle && value.baffle.type)
                              ) {
                                newValue.baffle = newValue.baffle || {};
                                newValue.baffle.params =
                                  saxophoneMouthpieceUtils.getDefaultBaffleParams(
                                    baffleType,
                                    value.saxophoneType
                                  );
                              }

                              onChange(newValue);
                            }}
                            disabled={!baffleTypeOptions.length}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <FieldArray name={`${name}.baffle.params`}>
                            {({ fields, ...rest }) =>
                              !!fields.length && (
                                <Fieldset
                                  legend="Paramètre(s) du plafond"
                                  margin="normal"
                                  sx={{
                                    ...(!value.baffle?.params?.length
                                      ? {
                                          paddingTop: 0,
                                          paddingBottom: 0,
                                          borderBottom: "none",
                                          borderLeft: "none",
                                          borderRight: "none",
                                        }
                                      : null),
                                  }}
                                >
                                  <Grid container>
                                    {fields.map((fieldName, idx) => (
                                      <Grid
                                        key={fieldName}
                                        item
                                        xs={12}
                                        sm={12 / fields.length}
                                      >
                                        <TextField
                                          type="number"
                                          name={fieldName}
                                          label={`Param #${idx + 1}`}
                                          {...floatFieldProps}
                                          InputProps={{
                                            inputProps: { step: 0.1 },
                                            endAdornment: (
                                              <InputAdornment position="end">
                                                mm
                                              </InputAdornment>
                                            ),
                                          }}
                                          validate={(baffleParam) => {
                                            if (
                                              !saxophoneMouthpieceUtils.isValidBaffleParam(
                                                baffleParam
                                              )
                                            )
                                              return "Paramètre invalide";
                                          }}
                                        />
                                      </Grid>
                                    ))}
                                  </Grid>
                                </Fieldset>
                              )
                            }
                          </FieldArray>
                        </Grid>
                      </Grid>{" "}
                    </Fieldset>
                  </Grid>

                  <Grid item xs={12}>
                    <Fieldset legend="Chambre" margin="normal">
                      <Grid container>
                        <Grid item xs={12} md={4}>
                          <SelectField
                            name={`${name}.chamber.type`}
                            label="Type de chambre"
                            required
                            disabled={!chamberTypeOptions.length}
                            validate={(chamberType) => {
                              if (
                                !saxophoneMouthpieceUtils.isValidChamberType(
                                  chamberType,
                                  value.saxophoneType
                                )
                              )
                                return "Type de chambre invalide";
                            }}
                          >
                            {chamberTypeOptions.map((chamberType) => (
                              <MenuItem
                                key={chamberType}
                                value={chamberType}
                                disabled={saxophoneMouthpieceUtils.isDisabledChamberType(
                                  chamberType,
                                  value.saxophoneType
                                )}
                              >
                                {chamberType}
                                {chamberType === "-" && " (Ronde)"}
                              </MenuItem>
                            ))}
                          </SelectField>
                        </Grid>

                        <Grid item xs={12} md={4}>
                          <SelectField
                            name={`${name}.chamber.sizeName`}
                            label="Taille de chambre"
                            required
                            disabled={!chamberSizeNameOptions.length}
                            validate={(chamberSizeName) => {
                              if (
                                !saxophoneMouthpieceUtils.isValidChamberSizeName(
                                  chamberSizeName
                                )
                              )
                                return "Taille de chambre invalide";
                            }}
                          >
                            {chamberSizeNameOptions.map((chamberSizeName) => (
                              <MenuItem
                                key={chamberSizeName}
                                value={chamberSizeName}
                              >
                                <Box component="span" sx={{ flexGrow: 1 }}>
                                  {chamberSizeName}
                                </Box>

                                {value.saxophoneType && (
                                  <Box
                                    component="span"
                                    sx={{
                                      color: "text.secondary",
                                      float: "right",
                                    }}
                                  >
                                    (
                                    {
                                      saxophoneMouthpieceUtils
                                        .CHAMBER_SIZES_CONFIG[
                                        value.saxophoneType
                                      ][chamberSizeName]
                                    }
                                    mm)
                                  </Box>
                                )}
                              </MenuItem>
                            ))}
                          </SelectField>
                        </Grid>

                        <Grid item xs={12} md={4}>
                          <TextField
                            name={`${name}.chamber.modifier`}
                            type="number"
                            required
                            {...integerFieldProps}
                            label="Modificateur de chambre"
                            helperText="Mantisse de la taille de chambre"
                            InputProps={{
                              inputProps: { step: 1, min: 0 },
                              endAdornment: (
                                <InputAdornment position="end">
                                  <Typography color="textSecondary">
                                    &times;10
                                    <sup>
                                      -
                                      {value.chamber && value.chamber.modifier
                                        ? value.chamber.modifier.toString()
                                            .length
                                        : 0}
                                    </sup>
                                    mm
                                  </Typography>
                                </InputAdornment>
                              ),
                            }}
                            validate={(chamberModifier) => {
                              if (
                                !saxophoneMouthpieceUtils.isValidChamberModifier(
                                  chamberModifier
                                )
                              )
                                return "Modificateur de taille de chambre invalide";
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Fieldset>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      name={`${name}.striations`}
                      type="number"
                      required
                      {...integerFieldProps}
                      label="Stries"
                      margin="normal"
                      inputProps={{ step: 1, min: 0 }}
                      validate={(striations) => {
                        if (
                          !saxophoneMouthpieceUtils.isValidStriations(
                            striations,
                            value.baffle && value.baffle.type,
                            value.saxophoneType
                          )
                        )
                          return "Nombre de stries invalide";
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      name={`${name}.tipOpening`}
                      type="number"
                      required
                      {...integerFieldProps}
                      label="Ouverture"
                      margin="normal"
                      InputProps={{
                        inputProps: {
                          step: 1,
                          min: tipOneningRange[0],
                          max: tipOneningRange[1],
                        },
                        endAdornment: (
                          <InputAdornment position="end">
                            {(() => {
                              const match = mouthpieceTipOpenings.saxophone[
                                value.saxophoneType
                              ]?.find(
                                (t) =>
                                  Math.round(t.mm * 100) === value.tipOpening
                              );

                              return (
                                (match ? `(${match.key}) ` : "") + "1/100mm"
                              );
                            })()}
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      name={`${name}.facing`}
                      type="number"
                      required
                      {...integerFieldProps}
                      label="Facing"
                      margin="normal"
                      InputProps={{
                        inputProps: { step: 1, min: 1 },
                        endAdornment: (
                          <InputAdornment position="end">mm</InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      name={`${name}.internalVolume`}
                      type="number"
                      required
                      {...floatFieldProps}
                      label="Volume interne"
                      margin="normal"
                      InputProps={{
                        inputProps: { step: 0.1 },
                        endAdornment: (
                          <InputAdornment position="end">
                            <Typography color="textSecondary">
                              cm
                              <sup>3</sup>
                            </Typography>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      name={`${name}.boreDiameter`}
                      type="number"
                      required
                      {...floatFieldProps}
                      label="Diamètre d’entrée (perce)"
                      margin="normal"
                      InputProps={{
                        inputProps: { step: 0.1 },
                        endAdornment: (
                          <InputAdornment position="end">mm</InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography
                      variant="h1"
                      sx={{
                        margin: (t) => t.spacing(2, 0),
                        fontFamily: "'Roboto Mono', monospace",
                      }}
                    >
                      {safe(saxophoneMouthpieceUtils.getNomenclature)(value)}
                    </Typography>
                  </Grid>
                </Grid>
              </Collapse>
            </Grid>
          </Grid>
        );
      }}
    </Field>
  );
}

SaxophoneMouthpieceField.defaultValues = {
  ...saxophoneMouthpieceUtils.applyMouthpieceDefaults({}),
};
